import Button, { ButtonTypes } from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal, { CloseDialogEvent } from "molecules/modals/modal";
import React, { useEffect, useState } from "react";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Heading from "atoms/typography/heading";
import PageRecord from "models/view-models/page-record";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import FileRecord from "models/view-models/file-record";
import { t } from "utilities/localization-utils";
import ImagePreviewField from "molecules/form-fields/image-preview-field";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import useFile from "utilities/hooks/domain/files/use-file";
import InputFormField from "molecules/form-fields/input-form-field";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";

interface ManageCustomLogoModalProps {
    page: PageRecord;
    isVisible: boolean;
    closeDialog: (event?: CloseDialogEvent) => void;
}

const BASE_CLASS_NAME = "c-manage-custom-logo-modal";

const ManageCustomLogoModal = (props: ManageCustomLogoModalProps) => {
    const { isVisible, closeDialog, page } = props;
    const [customLogo, setCustomLogo] = useState<FileRecord | undefined>(
        undefined
    );
    const [newAltText, setNewAltText] = useState<string>("");

    const [isValidAltText, setIsValidAltText] = useState<boolean>(true);

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    const { updateCustomLogo } = useCurrentPage();
    const { file } = useFile(page.customLogoFileId);

    const onAltTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewAltText(value);

        if (!isValidAltText && value.trim().length > 0) {
            setIsValidAltText(true);
        }
    };

    const onCustomLogoChange = (newcustomLogo?: FileRecord) => {
        setCustomLogo(newcustomLogo);
    };

    const onClickCancel = () => {
        closeDialog();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await updateCustomLogo(customLogo?.id, newAltText);
        closeDialog();
    };

    // Populate with current page details
    useEffect(() => {
        setNewAltText(page.customLogoAltText ?? "");
        setCustomLogo(file);
    }, [file, page.customLogoAltText]);

    return (
        <Modal
            stopEventPropagation
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={closeDialog}
            cssClassName={BASE_CLASS_NAME}
            isVisible={isVisible}
            onCloseComplete={onClickCancel}
            label={t("actionItem", {
                action: page.customLogoFileId ? t("edit") : t("add"),
                item: t("logo"),
            })}
            type={modalType}>
            <div className={`${BASE_CLASS_NAME}__content`}>
                <Heading cssClassName={`${BASE_CLASS_NAME}__heading`}>
                    {t("actionItem", {
                        action: page.customLogoFileId ? t("edit") : t("add"),
                        item: t("logo"),
                    })}
                    <Button
                        accessibleText={t("close")}
                        cssClassName={"-modal-close -icon"}
                        onClick={closeDialog}
                        size={ButtonSizes.Medium}
                        style={ButtonStyles.TertiaryAlt}>
                        <Icon type={Icons.Close} size={IconSizes.Large} />
                    </Button>
                </Heading>
                <form
                    className={`${BASE_CLASS_NAME}__form`}
                    onSubmit={handleSubmit}>
                    <ImagePreviewField
                        image={customLogo}
                        imageDestination={FileUploadDestination.NotebookImage}
                        onAdd={onCustomLogoChange}
                        onDelete={() => onCustomLogoChange(undefined)}
                    />
                    <div className={`${BASE_CLASS_NAME}__input-wrapper`}>
                        <InputFormField
                            cssClassName={`${BASE_CLASS_NAME}__input`}
                            onChange={onAltTextChange}
                            label={t("custom-logo-modal--alt-text-input-label")}
                            value={newAltText}
                            maxLength={60}
                            isValid={isValidAltText}
                            tooltipText={t(
                                "custom-logo-modal--alt-text-input-tooltip"
                            )}
                        />
                    </div>
                    <div className={`${BASE_CLASS_NAME}__buttons`}>
                        <Button
                            accessibleText={t("cancel")}
                            onClick={onClickCancel}
                            style={ButtonStyles.Secondary}>
                            {t("cancel")}
                        </Button>
                        <Button
                            accessibleText={t("save")}
                            style={ButtonStyles.Primary}
                            type={ButtonTypes.Submit}>
                            {t("save")}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default ManageCustomLogoModal;
